/**
 * P3-21: MetaphysicsPanel —— Metaphysics 区（迁移自 classes.ui.PrestigePanel，
 * js/prestige.js:615-657）。
 *
 * 显示条件（science.js:2790-2800）：
 * - metaphysics 已研究且 paragon > 0，或
 * - 任意 perk 已研究。
 *
 * 内容：
 * - 所有 prestige perks 按钮（PrestigeBtnController）；
 * - BurnParagon 按钮（ButtonModern，可见性依赖 paragon > 0）；
 * - TurnHGOff 按钮（ButtonModern，可见性依赖 holyGenocide 激活/开启）。
 */
import { useCallback } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { MetaphysicsPerkButton } from "./MetaphysicsPerkButton";
import { BurnParagonButton, TurnHGOffButton } from "./ModernActionButton";

interface PerkMeta {
  name: string;
  researched: boolean;
  unlocked: boolean;
}

interface MetaphysicsSnapshot {
  visible: boolean;
  perks: string[];
}

function computeSnapshot(game: GamePage | null): MetaphysicsSnapshot | null {
  if (!game) {
    return null;
  }
  const prestige = game.prestige as {
    perks: PerkMeta[];
  };
  const science = game.science as { get?: (name: string) => { researched?: boolean } };
  const metaphysics = science.get?.("metaphysics");
  const paragon = (game.resPool as { get?: (name: string) => { value?: number } }).get?.(
    "paragon",
  );

  let visible = false;
  if (metaphysics?.researched && (paragon?.value ?? 0) > 0) {
    visible = true;
  }
  if (!visible) {
    for (const perk of prestige.perks) {
      if (perk.researched) {
        visible = true;
        break;
      }
    }
  }

  return {
    visible,
    perks: prestige.perks.map((p) => p.name),
  };
}

function snapshotEq(
  a: MetaphysicsSnapshot | null,
  b: MetaphysicsSnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.visible === b.visible &&
    a.perks.length === b.perks.length &&
    a.perks.every((name, i) => name === b.perks[i])
  );
}

export function MetaphysicsPanel() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const burnParagon = useCallback(() => {
    getGame()?.discardParagon?.();
  }, []);

  const turnHGOff = useCallback(() => {
    const game = getGame();
    (game?.religion as { turnHGOff?: () => void })?.turnHGOff?.();
  }, []);

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <div className="metaphysicsPanel">
      {snap.perks.map((name) => (
        <MetaphysicsPerkButton key={name} perkName={name} />
      ))}
      <BurnParagonButton
        name={t("prestige.btn.burnParagon.label")}
        description={t("prestige.btn.burnParagon.desc")}
        handler={burnParagon}
      />
      <TurnHGOffButton
        name={t("prestige.btn.turnHGOff.label")}
        description={t("prestige.btn.turnHGOff.desc")}
        handler={turnHGOff}
      />
    </div>
  );
}
